import { useState } from "react";
import { FrameEmptyState } from "@/components/shapes/shared/frame-empty-state";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import { Spinner } from "@/components/ui";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { useFrameSurface } from "../shared/use-frame-surface";
import {
  PreviewBody,
  PreviewCheckMark,
  PreviewTitle,
} from "@/components/shapes/shared/card-preview-parts";
import { CARD_BODY_CONTENT } from "@/components/shapes/shared/frame-content-padding";
import { todoItemsFrom } from "./todo-items";
import { TodoEditRow } from "./todo-edit-row";
import { useTodoEdits } from "./use-todo-edits";
import { useT } from "@/i18n";
import { todoFrameMessages } from "@/i18n/messages/frames-todo";

/**
 * A checklist, opened as its card (Part 10): the title and the card's ○/●
 * rows, each with its detail, then "x of y done". A row's mark ticks it and
 * its text is typed in place; "Add a task" adds one (`useTodoEdits`). Agent
 * `frame.update` broadcasts re-render it.
 */
export function TodoFrame({ id, data, selected }: FrameProps) {
  const t = useT(todoFrameMessages);
  const frameSurface = useFrameSurface(id);
  const d = data;
  const edits = useTodoEdits({ frameId: id, content: d.content });
  // The row whose text takes focus next: one just added, or the row above
  // one just removed.
  const [focusId, setFocusId] = useState<string | null>(null);
  const items = todoItemsFrom(d.content?.items);
  const status = d.status;

  const doneCount = items.filter((item) => item.done === true).length;

  return (
    <div
      className={cn(
        frameSurface.className,
        "group relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="todo-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none nodrag min-h-0 flex-1 overflow-y-auto",
          CARD_BODY_CONTENT,
          selected && "nowheel",
        )}
        data-frame-content="true"
        onClick={(e) => e.stopPropagation()}
      >
        <PreviewBody
          density="regular"
          className={cn(items.length === 0 && "h-full")}
        >
          {status === "streaming" && items.length === 0 ? (
            <div
              className="flex items-center gap-2"
              data-testid="todo-frame-loading"
              aria-label={t("loadingTasks")}
            >
              <Spinner className="h-3 w-3" />
              <span className="text-card-muted">{t("gatheringTasks")}</span>
            </div>
          ) : status === "error" && items.length === 0 ? (
            <FrameEmptyState
              frameId={id}
              status={status}
              testId="todo-frame-empty"
            >
              {t("noTasks")}
            </FrameEmptyState>
          ) : (
            <>
              <PreviewTitle>
                <span data-testid="todo-frame-title">
                  {frameDisplayTitle(d)}
                </span>
              </PreviewTitle>
              {items.map((item, index) => (
                <TodoEditRow
                  key={item.id}
                  item={item}
                  edits={edits}
                  focused={focusId === item.id}
                  onFocusRow={setFocusId}
                  previousId={items[index - 1]?.id ?? null}
                />
              ))}
              <button
                type="button"
                className="flex items-start gap-[0.7em] text-left text-card-muted"
                onClick={() => setFocusId(edits.addAfter(null))}
                data-testid="todo-frame-add"
              >
                <PreviewCheckMark done={false} className="border-dashed" />
                {t("addTask")}
              </button>
              {items.length > 0 && (
                <p
                  className="mt-[0.4em] text-[0.84em] text-card-muted"
                  data-testid="todo-frame-progress"
                >
                  {t("progress", { done: doneCount, total: items.length })}
                </p>
              )}
            </>
          )}
        </PreviewBody>
      </div>
    </div>
  );
}
